import React, { useEffect } from 'react'
import { useToggle } from 'react-use'
import { ViewDataset } from '../DatasetInfo'
import { EditDatasetConfiguration } from './EditDatasetConfiguration'
import * as S from './DatasetInfo.style'
import { Heading } from './DatasetInfo.style'
import { Dataset, DatasetType } from '../../../../api/generated'

type DescriptionInfoProps = {
  datasetId: string
  updatedAt: number
  createdBy: string
  updatedBy: string
  name: string
  type: DatasetType
  powerBiId?: string
  isActiveVersion: boolean
  version: number
  versionLabel: string
  description?: string
  mandatoryDataPoints: string
  allDatasets: Dataset[]
  onChange: (description: string, mandatoryDataPoints: string, isActive: boolean, powerBiId: string) => Promise<void>
  refetch: () => void
}

export const DatasetInfo: React.FC<DescriptionInfoProps> = ({
  datasetId,
  updatedAt,
  createdBy,
  updatedBy,
  name,
  type,
  powerBiId,
  isActiveVersion,
  version,
  versionLabel,
  description,
  mandatoryDataPoints,
  allDatasets,
  onChange,
  refetch,
}) => {
  const [editForm, toggleEditForm] = useToggle(false)

  const handleOpenEditForm = () => toggleEditForm(true)
  const handleCloseEditForm = () => toggleEditForm(false)

  const handleSaveEditForm = async (
    description: string,
    mandatoryDataPoints: string,
    isActive: boolean,
    powerBiId: string,
  ) => {
    try {
      await onChange(description, mandatoryDataPoints, isActive, powerBiId)
      toggleEditForm(false)
    } catch (error) {
      throw error
    }
  }

  useEffect(() => {
    handleCloseEditForm()
  }, [datasetId])

  return (
    <div>
      <S.TopContainer>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Heading>{name}</Heading>
          <S.TypeBadge type={type}>{type}</S.TypeBadge>
        </div>

        <S.StatusBlock>
          <S.StatusTag isActive={isActiveVersion}>{isActiveVersion ? 'ACTIVATED' : 'DEACTIVATED'}</S.StatusTag>
          <S.SmallMeta>Created by: {createdBy || 'N/A'}</S.SmallMeta>
          <S.SmallMeta>Updated by: {updatedBy || 'N/A'}</S.SmallMeta>
        </S.StatusBlock>
      </S.TopContainer>

      {editForm ? (
        <EditDatasetConfiguration
          description={description}
          type={type}
          mandatoryDataPoints={mandatoryDataPoints}
          isActive={isActiveVersion}
          powerBiId={powerBiId}
          updatedAt={updatedAt}
          isMoreThanOneActive={
            allDatasets.map(dataset => dataset.activeVersionDescription).filter(desc => desc !== null).length > 1
          }
          onSubmit={handleSaveEditForm}
          onClose={handleCloseEditForm}
        />
      ) : (
        <ViewDataset
          datasetId={datasetId}
          name={name}
          description={description}
          mandatoryDataPoints={mandatoryDataPoints}
          allDatasets={allDatasets}
          onOpenEdit={handleOpenEditForm}
          refetch={refetch}
        />
      )}
    </div>
  )
}
